// widgets/StatusBar/StatusBar.tsx
// Bandeau fin affichant modele, % contexte, cout cumule, duree session.
// Lit model/totalCost depuis metrics.store, poll contextUsage via tRPC, calcule duree cote client.

import { useEffect, useRef, useState } from 'react'
import { useMetricsStore } from '@/shared/stores/metrics.store'
import { useStatusStore } from '@/shared/stores/status.store'
import { trpc } from '@/shared/lib/trpc'

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours >= 24) {
    const days = Math.floor(hours / 24)
    const remainingHours = hours % 24
    return `${days}j ${String(remainingHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

function formatCost(cost: number): string {
  return `${cost.toFixed(2)} $`
}

function contextColor(percentage: number | null): string {
  if (percentage === null) return 'var(--color-text-secondary)'
  if (percentage > 95) return 'var(--color-error)'
  if (percentage > 80) return 'var(--color-warning)'
  return 'var(--color-text-secondary)'
}

interface StatusBarProps {
  sessionId: string
  createdAt?: string | Date | null
}

export function StatusBar({ sessionId, createdAt }: StatusBarProps) {
  const model = useMetricsStore(s => s.sessions[sessionId]?.model ?? null)
  const totalCost = useMetricsStore(s => s.sessions[sessionId]?.totalCost ?? 0)
  const setContextUsage = useMetricsStore(s => s.setContextUsage)
  const sessionStatus = useStatusStore(s => s.statuses[sessionId] ?? 'idle')

  // Duree session — timer actif si session non fermee
  const [elapsed, setElapsed] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isClosed = sessionStatus === 'closed'

  useEffect(() => {
    if (!createdAt) return

    const startTime = new Date(createdAt).getTime()

    const tick = () => {
      setElapsed(Date.now() - startTime)
    }

    tick() // valeur initiale immediatement

    if (!isClosed) {
      intervalRef.current = setInterval(tick, 1000)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [createdAt, isClosed])

  // Context usage — poll tRPC toutes les 30s
  const contextQuery = trpc.sessions.contextUsage.useQuery(
    { sessionId },
    {
      refetchInterval: isClosed ? false : 30_000,
      enabled: !isClosed,
    },
  )

  // Sync contextUsage query data → metrics.store
  useEffect(() => {
    const percentage = contextQuery.data?.percentage ?? null
    setContextUsage(sessionId, percentage)
  }, [sessionId, contextQuery.data, setContextUsage])

  const contextPercentage = contextQuery.data?.percentage ?? null

  const separatorStyle = {
    color: 'var(--color-text-faint)',
    margin: '0 var(--space-2)',
  }

  return (
    <div
      style={{
        height: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-bg-muted)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-xs)',
        color: 'var(--color-text-secondary)',
        flexShrink: 0,
        borderTop: '1px solid var(--color-border)',
      }}
    >
      {/* Modele */}
      <span>{model ?? '\u2014'}</span>

      <span style={separatorStyle}>|</span>

      {/* % Contexte */}
      <span style={{ color: contextColor(contextPercentage) }}>
        {contextPercentage !== null ? `${Math.round(contextPercentage)}%` : '\u2014'}
        {' contexte'}
      </span>

      <span style={separatorStyle}>|</span>

      {/* Cout cumule */}
      <span>{formatCost(totalCost)}</span>

      <span style={separatorStyle}>|</span>

      {/* Duree */}
      <span>{createdAt ? formatDuration(elapsed) : '\u2014'}</span>
    </div>
  )
}
